/**
 * CaseExplorer — interactive per-case master-detail view for an offline eval
 * run. Replaces the static report.html iframe with a searchable / filterable /
 * sortable case list (left) + a selected-case detail panel (right): Input,
 * Agent answer, per-metric scorecards, and the execution Trace.
 *
 * Data tiers (see design):
 *   - The list comes from GetEvalCases (metrics + input/output; trace omitted to
 *     stay under the Lambda 6MB cap).
 *   - The trace is fetched lazily per case via GetEvalCase when a case is
 *     opened; if the server flagged traceTooLarge, we show a report fallback.
 */
import type { TFunction } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getEvalCases, getEvalCase } from "./preDeploymentApi";
import type { PreDeployCase } from "./preDeploymentApi";
import MarkdownRenderer from "../../../../components/ChatPanel/MarkdownRenderer";
import AskEvalAgent from "../../../components/AskEvalAgent/AskEvalAgent";
import "./CaseExplorer.css";

type StatusFilter = "all" | "passed" | "failed";
type SortKey = "status" | "score-desc" | "score-asc";

/** Overall case score = mean of its metric scores (the API has no single score;
 *  metrics are the source of truth). Empty metrics ⇒ null (rendered as —). */
function overallScore(c: PreDeployCase): number | null {
  if (!c.metrics.length) return null;
  return c.metrics.reduce((s, m) => s + m.score, 0) / c.metrics.length;
}

function rateClass(score: number): "high" | "mid" | "low" {
  if (score >= 0.85) return "high";
  if (score >= 0.7) return "mid";
  return "low";
}

function pct(n: number): string {
  return `${(n * 100).toFixed(0)}%`;
}

/** One tool invocation from the trace's tools_called list. */
interface TraceTool {
  name: string;
  input?: string;
  output?: string;
}

/** Render any value as a compact string for the in/out lines: objects/arrays as
 *  pretty JSON, scalars as-is. */
function traceValueToText(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

/** Secondary stats from the trace metadata (LLM calls, tokens, latency). The
 *  tool-call count is shown separately by the caller, so it's omitted here to
 *  avoid duplication. */
function traceStats(meta: any, t: TFunction<"eval">): string {
  if (!meta || typeof meta !== "object") return "";
  const parts: string[] = [];
  if (typeof meta.llm_calls === "number") {
    parts.push(t(meta.llm_calls === 1 ? "cases.llmCallsOne" : "cases.llmCallsMany", { n: meta.llm_calls }));
  }
  const tokens = (meta.total_input_tokens ?? 0) + (meta.total_output_tokens ?? 0);
  if (tokens) parts.push(t("cases.tokens", { n: tokens.toLocaleString() }));
  if (typeof meta.latency_seconds === "number") parts.push(`${meta.latency_seconds.toFixed(1)}s`);
  return parts.join(" · ");
}

/** Normalize the parsed trace into an ordered tool list + stats. Returns null
 *  when the trace isn't the expected {tools_called} shape (caller falls back). */
function normalizeTrace(trace: unknown, t: TFunction<"eval">): { tools: TraceTool[]; stats: string } | null {
  if (!trace || typeof trace !== "object") return null;
  const raw = trace as any;
  if (!Array.isArray(raw.tools_called)) return null;
  const tools: TraceTool[] = raw.tools_called.map((tc: any) => ({
    name: String(tc?.name ?? t("cases.unnamedTool")),
    input: traceValueToText(tc?.input_parameters),
    output: traceValueToText(tc?.output),
  }));
  return { tools, stats: traceStats(raw.metadata, t) };
}

/**
 * Trace panel — the execution as an ordered list of collapsible tool-call cards
 * (numbered 1..N), matching the email report's dark trace_block. Replaces the
 * old single JSON dump. The first call opens by default; the rest collapse so a
 * long run stays scannable. Unexpected shapes fall back to pretty JSON.
 */
function TraceView({ trace }: { trace: unknown }) {
  const { t } = useTranslation("eval");
  const norm = normalizeTrace(trace, t);
  if (!norm) {
    return <pre className="case-explorer__trace-json">{traceValueToText(trace)}</pre>;
  }
  if (norm.tools.length === 0) {
    return <div className="case-explorer__trace-empty">{t("cases.noToolCalls")}</div>;
  }
  const count = norm.tools.length;
  return (
    <div className="case-explorer__trace-tools">
      <div className="case-explorer__trace-stats">
        {t(count === 1 ? "cases.toolCallsOne" : "cases.toolCallsMany", { n: count })}
        {norm.stats && <span className="case-explorer__trace-stats-sep"> · {norm.stats}</span>}
      </div>
      {norm.tools.map((tool, i) => (
        <details key={i} className="case-explorer__trace-tool">
          <summary className="case-explorer__trace-tool-head">
            <span className="case-explorer__trace-num">{i + 1}.</span>
            <span className="case-explorer__trace-name">{tool.name}</span>
            <span className="case-explorer__trace-chev" aria-hidden="true">▾</span>
          </summary>
          {tool.input && (
            <div className="case-explorer__trace-line">
              <span className="case-explorer__trace-io">{t("cases.toolIn")}</span>
              <span className="case-explorer__trace-val">{tool.input}</span>
            </div>
          )}
          {tool.output && (
            <div className="case-explorer__trace-line">
              <span className="case-explorer__trace-io">{t("cases.toolOut")}</span>
              <span className="case-explorer__trace-val">{tool.output}</span>
            </div>
          )}
        </details>
      ))}
    </div>
  );
}

/**
 * Loading placeholder that mirrors the real layout — toolbar + the 60vh
 * master-detail split — so the detail page reserves the case explorer's full
 * height up front. Without this the loading state was a single short line, so
 * the verdict card and the HTML-report card rendered nearly touching, then the
 * page jumped when the API filled the gap. Shape only, wired to the shimmer.
 */
function CaseExplorerSkeleton() {
  const { t } = useTranslation("eval");
  return (
    <div className="case-explorer case-explorer--skeleton" aria-busy="true" aria-live="polite">
      <span className="case-explorer__sr-only">{t("cases.loading")}</span>
      {/* Toolbar row: search + filter segment + sort */}
      <div className="case-explorer__toolbar" aria-hidden="true">
        <div className="case-explorer__skel case-explorer__skel--search" />
        <div className="case-explorer__skel case-explorer__skel--segment" />
        <div className="case-explorer__skel case-explorer__skel--sort" />
      </div>
      {/* Master-detail split — same 60vh height as the loaded view */}
      <div className="case-explorer__split" aria-hidden="true">
        <div className="case-explorer__list">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="case-explorer__skel case-explorer__skel--row" />
          ))}
        </div>
        <div className="case-explorer__detail">
          <div className="case-explorer__skel case-explorer__skel--line case-explorer__skel--head" />
          <div className="case-explorer__skel case-explorer__skel--block" />
          <div className="case-explorer__skel case-explorer__skel--line" />
          <div className="case-explorer__skel case-explorer__skel--block case-explorer__skel--tall" />
        </div>
      </div>
    </div>
  );
}

export default function CaseExplorer({ runId }: { runId: string }) {
  const { t } = useTranslation("eval");
  const [cases, setCases] = useState<PreDeployCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortKey, setSortKey] = useState<SortKey>("status");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Lazily-fetched trace for the selected case, keyed by testCaseId so switching
  // back to an already-loaded case is instant.
  const [traceById, setTraceById] = useState<Record<string, PreDeployCase>>({});
  const [traceLoading, setTraceLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setCases([]);
    setSelectedId(null);
    setTraceById({});

    // Fetch ALL pages: GetEvalCases is paginated (default page is 100), so a run
    // with more cases than one page would otherwise silently drop the rest. Loop
    // on nextToken until the server stops returning one, accumulating every case.
    // The MAX_PAGES cap is a runaway-loop backstop, not an expected limit.
    (async () => {
      try {
        const all: PreDeployCase[] = [];
        let token: string | undefined = undefined;
        const MAX_PAGES = 100;
        for (let page = 0; page < MAX_PAGES; page++) {
          const res = await getEvalCases(runId, 100, token);
          all.push(...res.items);
          token = res.nextToken;
          if (!token) break;
        }
        if (cancelled) return;
        setCases(all);
        // Default selection: first failing case (they need attention), else first.
        const firstFail = all.find((c) => !c.passed);
        setSelectedId((firstFail ?? all[0])?.testCaseId ?? null);
      } catch (e: any) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [runId]);

  const visible = useMemo(() => {
    let out = cases.slice();
    if (statusFilter !== "all") {
      out = out.filter((c) => (statusFilter === "passed" ? c.passed : !c.passed));
    }
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      out = out.filter((c) => c.testCaseId.toLowerCase().includes(q));
    }
    out.sort((a, b) => {
      const sa = overallScore(a) ?? 0;
      const sb = overallScore(b) ?? 0;
      if (sortKey === "score-desc") return sb - sa;
      if (sortKey === "score-asc") return sa - sb;
      // "status": failing first, then by score ascending
      if (a.passed !== b.passed) return a.passed ? 1 : -1;
      return sa - sb;
    });
    return out;
  }, [cases, statusFilter, query, sortKey]);

  const selectedBase = cases.find((c) => c.testCaseId === selectedId) ?? null;
  // Prefer the trace-enriched copy once fetched.
  const selected = (selectedId && traceById[selectedId]) || selectedBase;

  // Fetch the trace for the selected case once (unless already cached).
  useEffect(() => {
    if (!selectedId || traceById[selectedId]) return;
    let cancelled = false;
    setTraceLoading(true);
    getEvalCase(runId, selectedId)
      .then((full) => !cancelled && setTraceById((prev) => ({ ...prev, [selectedId]: full })))
      .catch(() => {
        /* trace is best-effort; the detail still renders from the list copy */
      })
      .finally(() => !cancelled && setTraceLoading(false));
    return () => {
      cancelled = true;
    };
  }, [runId, selectedId, traceById]);

  const passed = cases.filter((c) => c.passed).length;
  const total = cases.length;

  if (loading) {
    return <CaseExplorerSkeleton />;
  }
  if (error) {
    return <p className="case-explorer__error">{error}</p>;
  }
  if (total === 0) {
    return (
      <p className="case-explorer__empty">
        {t("cases.noCases")}
      </p>
    );
  }

  return (
    <div className="case-explorer">
      {/* Toolbar */}
      <div className="case-explorer__toolbar">
        <div className="case-explorer__search">
          <span className="case-explorer__search-icon" aria-hidden="true">
            ⌕
          </span>
          <input
            className="case-explorer__search-input"
            placeholder={t("cases.search")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className="case-explorer__segment" role="group" aria-label={t("cases.filterLabel")}>
          {(["all", "passed", "failed"] as StatusFilter[]).map((s) => (
            <button
              key={s}
              className={`case-explorer__seg-btn${statusFilter === s ? " case-explorer__seg-btn--on" : ""}`}
              onClick={() => setStatusFilter(s)}
            >
              {t(`cases.${s}`)}
              {s !== "all" && (
                <span className="case-explorer__seg-count">
                  {s === "passed" ? passed : total - passed}
                </span>
              )}
            </button>
          ))}
        </div>

        <select
          className="case-explorer__sort"
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value as SortKey)}
          aria-label={t("cases.sortLabel")}
        >
          <option value="status">{t("cases.sortStatus")}</option>
          <option value="score-desc">{t("cases.sortHigh")}</option>
          <option value="score-asc">{t("cases.sortLow")}</option>
        </select>

        <span className="case-explorer__count">
          {t("cases.shown", { n: visible.length, total })}
        </span>
      </div>

      {/* Master-detail */}
      <div className="case-explorer__split">
        {/* Master: case list */}
        <div className="case-explorer__list">
          {visible.map((c) => {
            const s = overallScore(c);
            return (
              <button
                key={c.testCaseId}
                className={`case-explorer__row${c.testCaseId === selectedId ? " case-explorer__row--active" : ""}`}
                onClick={() => setSelectedId(c.testCaseId)}
              >
                <span
                  className={`case-explorer__dot case-explorer__dot--${c.passed ? "passed" : "failed"}`}
                  aria-hidden="true"
                />
                <span className="case-explorer__row-id">{c.testCaseId}</span>
                <span
                  className={`case-explorer__row-score${s != null ? ` case-explorer__row-score--${rateClass(s)}` : ""}`}
                >
                  {s != null ? pct(s) : "—"}
                </span>
              </button>
            );
          })}
          {visible.length === 0 && (
            <div className="case-explorer__list-empty">{t("cases.noMatch")}</div>
          )}
        </div>

        {/* Detail: selected case */}
        {selected && (
          <div className="case-explorer__detail">
            <div className="case-explorer__detail-head">
              <span
                className={`case-explorer__dot case-explorer__dot--${selected.passed ? "passed" : "failed"} case-explorer__dot--lg`}
                aria-hidden="true"
              />
              <code className="case-explorer__detail-id">{selected.testCaseId}</code>
              <span
                className={`case-explorer__verdict-pill case-explorer__verdict-pill--${selected.passed ? "passed" : "failed"}`}
              >
                {selected.passed ? t("cases.passed") : t("cases.failed")}
              </span>
              {(() => {
                const s = overallScore(selected);
                return s != null ? (
                  <span className={`case-explorer__detail-score case-explorer__detail-score--${rateClass(s)}`}>
                    {t("cases.overall", { pct: pct(s) })}
                  </span>
                ) : null;
              })()}
              <AskEvalAgent
                question={t("askAgent.offline.case", {
                  runId,
                  caseId: selected.testCaseId,
                  result: selected.passed ? t("cases.passed") : t("cases.failed"),
                  score: (() => {
                    const s = overallScore(selected);
                    return s != null ? pct(s) : "—";
                  })(),
                })}
              />
            </div>

            {selected.input && (
              <div className="case-explorer__block">
                <div className="case-explorer__block-label">{t("cases.input")}</div>
                <div className="case-explorer__bubble case-explorer__bubble--in">{selected.input}</div>
              </div>
            )}
            {selected.actualOutput && (
              <div className="case-explorer__block">
                <div className="case-explorer__block-label">{t("cases.answer")}</div>
                <div className="case-explorer__bubble case-explorer__bubble--out">
                  <MarkdownRenderer markdown={selected.actualOutput} />
                </div>
              </div>
            )}

            {/* Metric scorecards */}
            <div className="case-explorer__block">
              <div className="case-explorer__block-label">{t("cases.metrics")}</div>
              {selected.metrics.length === 0 ? (
                <div className="case-explorer__muted">{t("cases.noMetrics")}</div>
              ) : (
                <div className="case-explorer__metrics">
                  {selected.metrics.map((m) => (
                    <div
                      key={m.name}
                      className={`case-explorer__metric${m.success ? "" : " case-explorer__metric--fail"}`}
                    >
                      <div className="case-explorer__metric-top">
                        <span className="case-explorer__metric-name">
                          {m.name}
                          <AskEvalAgent
                            question={t("askAgent.offline.metric", {
                              runId,
                              caseId: selected.testCaseId,
                              metric: m.name,
                              score: m.score.toFixed(2),
                              threshold: m.threshold.toFixed(2),
                              reason: m.reason || "—",
                            })}
                          />
                        </span>
                        <span
                          className={`case-explorer__metric-score case-explorer__metric-score--${m.success ? "high" : "low"}`}
                        >
                          {m.score.toFixed(2)}
                          <span className="case-explorer__metric-thresh">/ {m.threshold.toFixed(2)}</span>
                        </span>
                      </div>
                      <div className="case-explorer__metric-bar">
                        <span
                          className={`case-explorer__metric-bar-fill case-explorer__metric-bar-fill--${m.success ? "high" : "low"}`}
                          style={{ width: pct(Math.min(m.score, 1)) }}
                        />
                        <span
                          className="case-explorer__metric-bar-tick"
                          style={{ left: pct(Math.min(m.threshold, 1)) }}
                        />
                      </div>
                      {m.reason && <div className="case-explorer__metric-reason">{m.reason}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Trace (lazy) */}
            <div className="case-explorer__block">
              <div className="case-explorer__block-label">{t("cases.trace")}</div>
              <div className="case-explorer__trace">
                {traceLoading && !traceById[selected.testCaseId] ? (
                  <div className="case-explorer__trace-empty">{t("cases.loadingTrace")}</div>
                ) : selected.traceTooLarge ? (
                  <div className="case-explorer__trace-empty">
                    {t("cases.traceTooLarge")}
                  </div>
                ) : selected.trace ? (
                  <TraceView trace={selected.trace} />
                ) : (
                  <div className="case-explorer__trace-empty">{t("cases.noTrace")}</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
